import { useQuery } from "@apollo/client";
import { useState, useEffect, useRef } from "react";
import { QR_GET_CONVERSATION, QR_LIST_MESSAGE } from "../graphQl/MessengerAPI";

export const useConversationWithMessages = (
  conversationId,
  paginationParams
) => {
  const [page, setPage] = useState(paginationParams.currentPage || 0);
  const [allMessages, setAllMessages] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Flags internes pour contrôler le comportement
  const isRefetchingAll = useRef(false);
  const hasLoadedMore = useRef(false);

  // 🔹 Réinitialiser tout quand la conversation change
  useEffect(() => {
    setPage(paginationParams.currentPage || 0);
    setAllMessages([]);
    isRefetchingAll.current = false;
    hasLoadedMore.current = false;
  }, [conversationId, paginationParams.currentPage]);

  // --- Requête conversation ---
  const {
    data: conversationData,
    loading: conversationLoading,
    error: conversationError,
    refetch: refetchConversation,
  } = useQuery(QR_GET_CONVERSATION, {
    variables: { conversationId },
    fetchPolicy: "cache-first",
  });

  // --- Requête messages ---
  const {
    data: messagesData,
    loading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(QR_LIST_MESSAGE, {
    variables: {
      conversationId,
      paginationParams: { ...paginationParams, currentPage: page },
    },
    skip: !conversationId,
    fetchPolicy: "cache-and-network",
  });

  const conversationBusinessError =
    conversationData?.QR_GET_CONVERSATION?.error ?? null;
  const messagesBusinessError = messagesData?.QR_LIST_MESSAGE?.error ?? null;
  const conversation = conversationData?.QR_GET_CONVERSATION?.data ?? null;
  const messagesResponse = messagesData?.QR_LIST_MESSAGE?.data ?? {};
  const messagesPage = messagesResponse.content ?? [];

  // --- Gestion des messages en fonction du contexte ---
  useEffect(() => {
    if (!messagesPage.length) {
      if (page === 0 && !isLoadingMore && !isRefetchingAll.current) {
        setAllMessages([]);
      }
      if (isLoadingMore) {
        setIsLoadingMore(false);
      }
      return;
    }

    // 🔹 Cas 1 : refetchAll (nouveau message envoyé ou refresh complet)
    if (isRefetchingAll.current) {
      setAllMessages(messagesPage);
      isRefetchingAll.current = false;
      hasLoadedMore.current = false;
      return;
    }

    // 🔹 Cas 2 : loadMore (pagination)
    if (isLoadingMore) {
      setAllMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newOnes = messagesPage.filter((m) => !existingIds.has(m.id));
        return [...newOnes, ...prev]; // On ajoute les plus anciens au début
      });
      setIsLoadingMore(false);
      hasLoadedMore.current = true;
      return;
    }

    // 🔹 Cas 3 : chargement initial
    setAllMessages(messagesPage);
  }, [messagesPage]);

  const loading = conversationLoading || messagesLoading;
  const error =
    conversationError ||
    messagesError ||
    conversationBusinessError ||
    messagesBusinessError;

  // 🔄 Rafraîchissement complet (après envoi de message)
  const refetchAll = async () => {
    // ✅ Même si on a fait un loadMore avant, on repart sur un état clean
    isRefetchingAll.current = true;
    setPage(0);
    await Promise.all([refetchConversation(), refetchMessages()]);
  };

  // 🔼 Charger plus de messages (pagination)
  const loadMoreMessages = () => {
    if (page + 1 < (messagesResponse.totalPages ?? 1)) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  return {
    conversation,
    messages: allMessages,
    totalElements: messagesResponse.totalElements ?? 0,
    totalPages: messagesResponse.totalPages ?? 0,
    currentPage: page,
    loading,
    error,
    refetchConversation,
    refetchMessages,
    refetchAll,
    loadMoreMessages,
  };
};
